import produce from 'immer';
import { combineReducers } from 'redux';
import {
  AssistantAction,
  AssistantActionType,
  AssistantEditingDetails,
} from '../../actions/assistantActions';

const loading = (state = false, action: AssistantAction) => {
  switch (action.type) {
    case AssistantActionType.LIST_ASSISTANTS:
      return true;
    case AssistantActionType.LIST_ASSISTANTS_OK:
    case AssistantActionType.LIST_ASSISTANTS_ERROR:
      return false;
    default:
      return state;
  }
};

const loadingError = (state = '', action: AssistantAction) => {
  switch (action.type) {
    case AssistantActionType.LIST_ASSISTANTS:
      return '';
    case AssistantActionType.LIST_ASSISTANTS_ERROR:
      return action.error;
    default:
      return state;
  }
};

const creating = (state = false, action: AssistantAction) => {
  switch (action.type) {
    case AssistantActionType.CREATE_ASSISTANT:
      return true;
    case AssistantActionType.CREATE_ASSISTANT_OK:
    case AssistantActionType.CREATE_ASSISTANT_ERROR:
      return false;
    default:
      return state;
  }
};

const createError = (state = '', action: AssistantAction) => {
  switch (action.type) {
    case AssistantActionType.CREATE_ASSISTANT:
      return '';
    case AssistantActionType.CREATE_ASSISTANT_ERROR:
      return action.error;
    default:
      return state;
  }
};

type LoadingState = { loading: boolean; loadingError: string };
type DeletingState = { deleting: boolean; deleteError: string };
type StartingState = { starting: boolean; startingError: string };
type EditingStates = {
  [editingStatusKey in AssistantEditingDetails['statusKey']]: boolean;
};
type EditingErrors = {
  [editingErrorKey in AssistantEditingDetails['errorKey']]: string;
};

export type AssistantState = LoadingState &
  DeletingState &
  EditingStates &
  EditingErrors &
  StartingState;

interface AssistantStatesById {
  [id: string]: AssistantState;
}

const byId = (state: AssistantStatesById = {}, action: AssistantAction) =>
  produce(state, (draft) => {
    switch (action.type) {
      case AssistantActionType.GET_ASSISTANT: {
        draft[action.assistantId] = {
          ...draft[action.assistantId],
          loading: true,
          loadingError: '',
        };
        break;
      }
      case AssistantActionType.GET_ASSISTANT_ERROR: {
        draft[action.assistantId].loading = false;
        draft[action.assistantId].loadingError = action.error;
        break;
      }
      case AssistantActionType.GET_ASSISTANT_OK: {
        draft[action.assistantId].loading = false;
        break;
      }
      case AssistantActionType.EDIT_ASSISTANT: {
        draft[action.assistantId][action.statusKey] = true;
        draft[action.assistantId][action.errorKey] = '';
        break;
      }
      case AssistantActionType.EDIT_ASSISTANT_OK: {
        draft[action.assistantId][action.statusKey] = false;
        break;
      }
      case AssistantActionType.EDIT_ASSISTANT_ERROR: {
        draft[action.assistantId][action.statusKey] = false;
        draft[action.assistantId][action.errorKey] = action.error;
        break;
      }
      case AssistantActionType.DELETE_ASSISTANT: {
        draft[action.assistantId].deleting = true;
        draft[action.assistantId].deleteError = '';
        break;
      }
      case AssistantActionType.DELETE_ASSISTANT_OK: {
        draft[action.assistantId].deleting = false;
        break;
      }
      case AssistantActionType.DELETE_ASSISTANT_ERROR: {
        draft[action.assistantId].deleting = false;
        draft[action.assistantId].deleteError = action.error;
        break;
      }
      case AssistantActionType.LIST_ASSISTANTS_OK: {
        action.data.forEach((proc) => {
          draft[proc.id] = {} as AssistantState;
        });

        break;
      }
      case AssistantActionType.CREATE_ASSISTANT_OK: {
        draft[action.data.id] = {} as AssistantState;
        break;
      }
      default:
        return state;
    }
  });

export default combineReducers({
  loading,
  loadingError,
  creating,
  createError,
  byId,
});
